import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { merge, Observable, of, timer } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

/**
 * CVA async validate that does not hang when the inner form is already
 * VALID/INVALID or when statusChanges is suppressed by emitEvent: false.
 *
 * Only INVALID reports invalidForm. PENDING is waited out; DISABLED is not
 * treated as invalid (item-select enable/disable).
 */
export function catalogImageCvaValidate(
  form: AbstractControl | null | undefined,
  destroy$: Observable<unknown>,
  invalidMessage: string,
): Observable<ValidationErrors | undefined> {
  if (!form) {
    return of(undefined);
  }

  if (form.status !== 'PENDING') {
    return of(
      form.status === 'INVALID'
        ? {
            invalidForm: {
              valid: false,
              message: invalidMessage,
            },
          }
        : undefined,
    );
  }

  return merge(form.statusChanges, timer(0, 50).pipe(map(() => form.status))).pipe(
    filter((status) => status !== 'PENDING'),
    takeUntil(destroy$),
    take(1),
    map(() =>
      form.status === 'INVALID'
        ? {
            invalidForm: {
              valid: false,
              message: invalidMessage,
            },
          }
        : undefined,
    ),
  );
}

export function whenControlNotPending(
  control: AbstractControl | null | undefined,
  destroy$: Observable<unknown>,
): Observable<void> {
  if (!control || control.status !== 'PENDING') {
    return of(undefined);
  }

  return merge(control.statusChanges, timer(0, 50).pipe(map(() => control.status))).pipe(
    filter((status) => status !== 'PENDING'),
    takeUntil(destroy$),
    take(1),
    map(() => undefined),
  );
}

/** Attach image async validators only while the value is a File, so URLs/empty stay off PENDING. */
export function syncFileOnlyAsyncValidator(
  control: AbstractControl,
  asyncValidator?: AsyncValidatorFn | null,
): boolean {
  const isFile = control.value instanceof File;
  const hasAsync = !!control.asyncValidator;

  if (isFile && asyncValidator && !hasAsync) {
    control.setAsyncValidators(asyncValidator);
    return true;
  }

  if (!isFile && hasAsync) {
    control.clearAsyncValidators();
    return true;
  }

  return false;
}
